import { ZT_ROOT_ADDRESS } from '@src/constants/urls';
import { isProduction } from '@src/constants';
import ServiceResponseError from '@src/error/ServiceResponseError';
import { getMockPassportAccount } from '../mock-passport-account';
import BusinessServiceFactory from '../business-service-factory';

const GET_SERVERS_PATH = '/core/jxsjmobile4/get_servers';
const GET_ROLES_PATH = '/core/jxsjmobile4/get_roles';
const INVALID_SERVER_RESPONSE_MESSAGE = 'invalid server response';
const INVALID_ROLE_RESPONSE_MESSAGE = 'invalid role response';
const SERVER_ID_REQUIRED_MESSAGE = 'serverId is required';

/**
 * Keeps public API shape failures distinguishable from backend ServiceResponseError failures.
 * @param {string} message Contract-specific failure message.
 * @returns {Error} Local contract error.
 */
function createContractError(message) {
  return new Error(message);
}

/**
 * Preserves documented numeric identifiers while rejecting empty or lossy object coercion.
 * @param {string|number} value Raw public API identifier or name.
 * @param {string} errorMessage Contract-specific failure message.
 * @returns {string} Stable non-empty string.
 */
function normalizeRequiredString(value, errorMessage) {
  if ((typeof value !== 'string' && typeof value !== 'number') || (typeof value === 'number' && !Number.isFinite(value)) || String(value).trim().length === 0) {
    throw createContractError(errorMessage);
  }
  return String(value);
}

/**
 * Accepts finite numbers and complete numeric strings because the public API returns both forms.
 * @param {number|string} value Raw documented numeric field.
 * @param {string} errorMessage Contract-specific failure message.
 * @returns {number} Stable finite number.
 */
function normalizeNumber(value, errorMessage) {
  const isFiniteNumber = typeof value === 'number' && Number.isFinite(value);
  const isNumericString = typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value));

  if (!isFiniteNumber && !isNumericString) {
    throw createContractError(errorMessage);
  }
  return Number(value);
}

/** Maps get_servers fields verified against the public test endpoint on 2026-09-09. */
function normalizeServerOption(server) {
  if (!server || typeof server !== 'object' || Array.isArray(server)) {
    throw createContractError(INVALID_SERVER_RESPONSE_MESSAGE);
  }

  const status = normalizeNumber(server.status, INVALID_SERVER_RESPONSE_MESSAGE);
  if (status !== 0 && status !== 1) {
    throw createContractError(INVALID_SERVER_RESPONSE_MESSAGE);
  }

  return {
    serverId: normalizeRequiredString(server.server_id, INVALID_SERVER_RESPONSE_MESSAGE),
    serverName: normalizeRequiredString(server.server_name, INVALID_SERVER_RESPONSE_MESSAGE),
    status
  };
}

/** Maps documented get_roles snake_case fields into the model consumed by the binding picker. */
function normalizeRoleOption(role) {
  if (!role || typeof role !== 'object' || Array.isArray(role)) {
    throw createContractError(INVALID_ROLE_RESPONSE_MESSAGE);
  }

  return {
    roleId: normalizeRequiredString(role.role_id, INVALID_ROLE_RESPONSE_MESSAGE),
    roleName: normalizeRequiredString(role.role_name, INVALID_ROLE_RESPONSE_MESSAGE),
    serverId: normalizeRequiredString(role.server_id, INVALID_ROLE_RESPONSE_MESSAGE),
    serverName: normalizeRequiredString(role.server_name, INVALID_ROLE_RESPONSE_MESSAGE),
    level: normalizeNumber(role.level, INVALID_ROLE_RESPONSE_MESSAGE),
    powerFight: normalizeNumber(role.power_fight, INVALID_ROLE_RESPONSE_MESSAGE),
    createTime: normalizeNumber(role.create_time, INVALID_ROLE_RESPONSE_MESSAGE)
  };
}

/**
 * Applies the public endpoint envelope without duplicating the generated activity service contract.
 * @param {Object} response Raw JXSJ4 service envelope.
 * @returns {Object|Promise<never>} Success payload or a rejected contract/backend error.
 */
function unwrapServiceResponse(response) {
  if (!response || response.status !== 1) {
    return Promise.reject(new ServiceResponseError(response || { message: 'invalid service response' }));
  }
  if (!response.data || typeof response.data !== 'object' || Array.isArray(response.data)) {
    return Promise.reject(createContractError('invalid service response'));
  }
  return response.data;
}

export class Jxsj4RoleServiceFactory extends BusinessServiceFactory {
  static getDefaultInstance() {
    if (!Jxsj4RoleServiceFactory._defaultServiceInstance) {
      Jxsj4RoleServiceFactory._defaultServiceInstance = new Jxsj4RoleServiceFactory().createService();
    }
    return Jxsj4RoleServiceFactory._defaultServiceInstance;
  }

  constructor() {
    super({
      queueMap: {},
      optimizeConcurrentSameRequest: false,
      optimizeUsingCache: 0,
      requestWithUtmSourceFieldName: ''
    });
    this.handleTestPassportAccount();
  }

  /**
   * Injects the URL-specified simulated passport into core JXSJ4 requests.
   * `get_roles` requires a login identity and returns -10402 anonymously; verified on 2026-09-09
   * that `?_mock_passport_account=<full-account>` (e.g. 18615625506) makes it return that account's
   * roles, while `get_servers` ignores the extra parameter harmlessly. These public endpoints have
   * no `_mock_passport_account` parameter slot, so the injection adds the key directly — the same
   * unconditional strategy the ZT factory applies to its slot-less `team_config`.
   */
  handleTestPassportAccount() {
    if (isProduction) {
      return;
    }

    this.beforeRequests.push(({ url, params }) => {
      const mockPassportAccount = getMockPassportAccount();
      if (!mockPassportAccount) {
        return undefined;
      }

      return {
        url,
        params: {
          ...params,
          _mock_passport_account: mockPassportAccount
        }
      };
    });
  }

  /**
   * Creates the public JXSJ4 role API without adding it to the activity-specific generated ZT contract.
   * @returns {{getServers: Function, getRoles: Function}} Raw public API service.
   */
  createService() {
    return {
      getServers: () => this.request(`${ZT_ROOT_ADDRESS}${GET_SERVERS_PATH}`, {}).then(unwrapServiceResponse),
      getRoles: serverId => {
        let normalizedServerId;
        try {
          normalizedServerId = normalizeRequiredString(serverId, SERVER_ID_REQUIRED_MESSAGE);
        } catch (error) {
          return Promise.reject(error);
        }
        return this.request(`${ZT_ROOT_ADDRESS}${GET_ROLES_PATH}`, { server_id: normalizedServerId }).then(unwrapServiceResponse);
      }
    };
  }
}

/**
 * Loads selectable servers and maps the public response to stable ServerOption values.
 * Disabled servers retain their status during validation but are excluded from the selectable result.
 * @param {Object} options Adapter dependencies.
 * @param {Function} options.provider Optional raw server provider for unit tests.
 * @returns {Promise<Array<{serverId: string, serverName: string, status: number}>>} Selectable servers.
 */
export function loadServerOptions(options = {}) {
  const provider = options.provider || (() => Jxsj4RoleServiceFactory.getDefaultInstance().getServers());

  return Promise.resolve()
    .then(() => provider())
    .then(rawData => {
      if (!rawData || typeof rawData !== 'object' || Array.isArray(rawData) || !Array.isArray(rawData.server)) {
        throw createContractError(INVALID_SERVER_RESPONSE_MESSAGE);
      }
      return rawData.server.map(normalizeServerOption).filter(server => server.status === 1);
    });
}

/**
 * Loads one server's roles and maps the public response to stable RoleOption values.
 * @param {string|number} serverId Server selected from ServerOption.
 * @param {Object} options Adapter dependencies.
 * @param {Function} options.provider Optional raw role provider for unit tests.
 * @returns {Promise<Array<Object>>} Stable roles; an empty backend list remains empty.
 */
export function loadRoleOptions(serverId, options = {}) {
  let normalizedServerId;
  try {
    normalizedServerId = normalizeRequiredString(serverId, SERVER_ID_REQUIRED_MESSAGE);
  } catch (error) {
    return Promise.reject(error);
  }

  const provider = options.provider || (selectedServerId => Jxsj4RoleServiceFactory.getDefaultInstance().getRoles(selectedServerId));

  return Promise.resolve()
    .then(() => provider(normalizedServerId))
    .then(rawData => {
      if (!rawData || typeof rawData !== 'object' || Array.isArray(rawData) || !Array.isArray(rawData.roles)) {
        throw createContractError(INVALID_ROLE_RESPONSE_MESSAGE);
      }
      return rawData.roles.map(normalizeRoleOption);
    });
}

export default Jxsj4RoleServiceFactory;
